import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Context
import { useMotivators } from "@/Motivators/context/Common/MotivatorsContext";

// Third Party
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import PropTypes from "prop-types";
import { compact } from "lodash-es";

// Helpers
import {
	dateValidation,
	emptyValidation,
	overlapsRangeValidation,
} from "@/Layouts/components/Tables/DataSmartTable/validation/TableValidations";
import RowFeedback from "@/Common/helpers/RowFeedback";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { getPositionsByDepartments } from "@/Common/helpers/Positions";

// Hooks/Configs
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useLocation } from "@Common/hooks/Location/useLocation";

// APIs
import { getUsersSelect } from "@/Motivators/api/common";
import { createTrainingItem, deleteTrainingItems, updateTrainingItem } from "@/Motivators/api/training";

// Components
import TableInputValue from "@/Layouts/components/InputComponents/TableInputValue";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import DataTableDateTimePicker from "@/Layouts/components/DateTimePicker/DataTableDateTimePicker";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import HelpTooltip from "@/Layouts/components/Tables/DataSmartTable/additional/HelpTooltip";

// MUI
import { GridActionsCellItem, GridRowModes, useGridApiContext, useGridApiRef } from "@mui/x-data-grid-premium";

// Icons
import CancelIcon from "@mui/icons-material/Close";
import { SaveIconComponent } from "@/Layouts/components/Tables/DataSmartTable/additional/SaveIconComponent";

// Constants
import {
	COLUMN_RESIZE_FACTOR,
	ONE_HUNDRED_EIGHTY,
	ONE_HUNDRED_FIFTY,
	ONE_HUNDRED_TWENTY,
	TAILWIND_FOR_DATA_SMART_TABLE,
} from "@/Constants/tables";
import actionsList from "@/Constants/actionsList";
import { DATE_FORMAT } from "@/Constants/date";
import { HELP_TOOLTIP_TITLE } from "@/Motivators/constants";

const INPUT_RANGES = {
	Dollar: { min: 0.01, max: 10000 },
	Duration: { min: 0.1, max: 160 },
};

function InputEditPayCell(props) {
	const apiRef = useGridApiContext();
	const { id, field, hasFocus } = props;
	const [value, setValue] = useState(props.value?.value);
	const [inputType, setInputType] = useState(props.value?.input || "Dollar");

	const handleChangeType = (_valueData, input) => setInputType(input);

	return (
		<TableInputValue
			isEditingMode={hasFocus}
			isRequiredOnChange={true}
			tooltipMessage={props?.error ? props?.errorMessage : ""}
			tooltipPlacement={"top"}
			value={{
				value,
				input: inputType,
			}}
			defaultType={inputType}
			onChange={(updated) => setValue(updated)}
			onChangeType={handleChangeType}
			data={[
				{
					type: "Dollar",
					min: 0.01,
					max: 10000,
					decimalsEditable: true,
					decimals: 2,
				},
				{
					type: "Duration",
					min: 0.1,
					max: 160,
					decimalsEditable: true,
					decimals: 2,
				},
			]}
			onBlur={(updatedValue, input) => {
				setValue(updatedValue);
				apiRef.current.setEditCellValue({ id, field, value: { value: updatedValue, input } });
			}}
			sx={{ height: "27px" }}
			error={!value}
		/>
	);
}

InputEditPayCell.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	field: PropTypes.string,
	value: PropTypes.shape({
		value: PropTypes.number,
		input: PropTypes.string,
	}),
	hasFocus: PropTypes.bool,
	error: PropTypes.bool,
	errorMessage: PropTypes.string,
};

function InputEditDatetimeCell(props) {
	const apiRef = useGridApiContext();
	const { id, field, startDate, endDate, startDateRange, endDateRange, error, setRowEffectiveDate, assocAlreadySelected } = props;
	const minDate = startDate && moment(startDate).isValid() ? moment(startDate) : moment(startDateRange);
	const maxDate = endDate && moment(endDate).isValid() ? moment(endDate) : moment(endDateRange);

	const [dateValue, setDateValue] = useState(props.value);

	const validateOverlappingDates = (value) => {
		return assocAlreadySelected?.some((assoc) => {
			let rowEffectiveTo;
			let rowEffectiveFrom;

			if (props?.field === "effectiveDateFrom") {
				rowEffectiveTo = props?.rowEffectiveDateTo;
				rowEffectiveFrom = value;
			} else {
				rowEffectiveTo = value;
				rowEffectiveFrom = props?.rowEffectiveDateFrom;
			}

			return overlapsRangeValidation({
				rowEffectiveFrom,
				rowEffectiveTo,
				effectiveDateFrom: assoc?.effectiveDateFrom,
				effectiveDateTo: assoc?.effectiveDateTo,
			});
		});
	};

	const onChangeHandle = (value) => {
		let inputValue;
		if (field === "effectiveDateTo") {
			inputValue = moment(value).endOf("day").format();
		} else {
			inputValue = moment(value).startOf("day").format();
		}

		const valueOverlaps = validateOverlappingDates(inputValue);
		props?.setOverlappingError(valueOverlaps);

		// `debounceMs` is important to avoid the input to be updated too often.
		apiRef.current.setEditCellValue({ id, field, value: moment(inputValue), debounceMs: 200 });
		setDateValue(moment(inputValue));
		setRowEffectiveDate(moment(inputValue));
	};

	useEffect(() => {
		const valueOverlaps = assocAlreadySelected?.some((assoc) =>
			overlapsRangeValidation({
				effectiveDateFrom: assoc?.effectiveDateFrom,
				effectiveDateTo: assoc?.effectiveDateTo,
				rowEffectiveFrom: props.rowEffectiveDateFrom,
				rowEffectiveTo: props.rowEffectiveDateTo,
			})
		);

		props?.setOverlappingError(valueOverlaps);

		if (props.value) {
			setDateValue(props.value);
			if (field === "effectiveDateFrom") {
				setRowEffectiveDate(props.value);
			}
			if (field === "effectiveDateTo") {
				setRowEffectiveDate(props.value);
			}
		}

		return () => {
			setRowEffectiveDate();
		};
	}, [props.value, props.selectedItem, assocAlreadySelected]);

	return (
		<DataTableDateTimePicker
			value={dateValue}
			hideTabs
			minDateTime={moment(minDate)}
			maxDateTime={moment(maxDate)}
			defaultCalendarMonth={moment(minDate)}
			disableIgnoringDatePartForTimeValidation
			disableHighlightToday
			format={DATE_FORMAT.monthAbbrDatePadded}
			error={error}
			onChange={onChangeHandle}
			views={["year", "month", "day"]}
			viewRenderers={{
				hours: null,
				minutes: null,
				seconds: null,
			}}
			tooltipMessage={error ? props?.errorMessage : ""}
			tooltipPlacement={"top"}
		/>
	);
}

InputEditDatetimeCell.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	field: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	startDateRange: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	endDateRange: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	setOverlappingError: PropTypes.func.isRequired,
	rowEffectiveDateTo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	rowEffectiveDateFrom: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	error: PropTypes.bool,
	setRowEffectiveDate: PropTypes.func.isRequired,
	assocAlreadySelected: PropTypes.arrayOf(
		PropTypes.shape({
			effectiveDateFrom: PropTypes.PropTypes.string,
			effectiveDateTo: PropTypes.string,
		})
	),
	errorMessage: PropTypes.string,
	selectedItem: PropTypes.object,
};

function InputEditUserCell(props) {
	const apiRef = useGridApiContext();
	const { id, field, error, setAssocAlreadySelected } = props;
	const location = useSelector((state) => state.location.location);
	const [open, setOpen] = useState(false);
	const [recipient, setRecipient] = useState(null);

	const servicePositions = getPositionsByDepartments(["Parts", "Service", "Body Shop"]);

	const {
		data: recipientData,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ["userTrainingInput", location?.locationId, servicePositions],
		queryFn: async () => getUsersSelect(location?.locationId, servicePositions),
		staleTime: Infinity,
		enabled: !!servicePositions?.length,
		placeholderData: [],
	});

	const handleChange = (values) => {
		if (values) {
			const alreadySelected = props?.rows?.filter((row) => row?.userId?._id === values._id && row?._id !== id);
			setAssocAlreadySelected(alreadySelected);

			// `debounceMs` is important to avoid the input to be updated too often.
			apiRef.current.setEditCellValue({ id, field, value: values, debounceMs: 200 });
			setRecipient(values);
		}
	};

	useEffect(() => {
		if (!props.row.userId) {
			return;
		}
		setRecipient(props.row.userId);
	}, [props.row.userId]);

	return (
		<SmartSelector
			showBy={"fullName"}
			sortBy={"fullName"}
			options={recipientData}
			selected={recipient}
			inputProps={{
				placeholder: "Recipients",
				classes:
					"[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] [&_.MuiInputBase-root_.MuiInputBase-input]:!rounded-none",
			}}
			listBoxProps={{
				sx: { maxHeight: "223px" },
			}}
			error={error && !recipient}
			loading={isLoading || isFetching}
			onChangeAction={handleChange}
		/>
	);
}

InputEditUserCell.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	field: PropTypes.string.isRequired,
	error: PropTypes.bool,
	setAssocAlreadySelected: PropTypes.func.isRequired,
	rows: PropTypes.arrayOf(
		PropTypes.shape({
			userId: PropTypes.shape({
				_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			}),
		})
	),
	row: PropTypes.shape({
		userId: PropTypes.shape({
			_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		}),
	}),
};

function TrainingTable(props) {
	const apiRef = useGridApiRef();
	const user = useSelector((state) => state.user.user);

	const newlyAddedRow = useRef(null);

	const { timezone } = useLocation();
	// Motivators Context
	const { locationId, subscriptions, motivatorType, isLoading } = useMotivators();
	const queryClient = useQueryClient();
	const { getSubscription } = useSubscriptions();

	// Auth
	const { useAuth } = useAuthorization();
	const editTraining = useAuth(actionsList.EDIT_SERVICES_TRAINING);
	const isColEditable = editTraining && props?.currentMonth;

	/***************************************** SUBSCRIPTIONS *****************************************/
	const serviceTrainingSubscriptions = getSubscription([motivatorType + "Body", locationId, props.commissionCycleId]);

	serviceTrainingSubscriptions?.subscriptions?.onCreateItem?.setCallback((result) => {
		const updatedItems = result.data?.[subscriptions.get("onCreateItem")?.getter]?.items;

		const trainingAdded = updatedItems?.[updatedItems.length - 1];

		if (trainingAdded) {
			queryClient.setQueryData(["serviceTraining", props?.commissionCycleId, locationId], (cachedData) => {
				return {
					...cachedData,
					items: [...(cachedData?.items || []), trainingAdded],
				};
			});
			cleanUpEditionMode(trainingAdded);
		}
	});

	serviceTrainingSubscriptions?.subscriptions?.onDeleteItem?.setCallback((result) => {
		const trainingDeleted = result.data?.[subscriptions.get("onDeleteItem")?.getter]?.items || [];

		// Get the row that is currently in edition mode
		const rowInEditionModeId = Object.keys(rowModesModel).find((key) => rowModesModel[key].mode === GridRowModes.Edit);
		const rowInEditionMode = rows.find((row) => row._id === rowInEditionModeId);

		if (trainingDeleted?.length) {
			queryClient.setQueryData(["serviceTraining", props?.commissionCycleId, locationId], (cachedData) => {
				return {
					...cachedData,
					items: cachedData?.items?.filter((item) => !trainingDeleted.some((el) => el._id === item._id)),
				};
			});

			// If the row that is currently in edition mode is deleted, clean up the edition mode
			if (rowInEditionMode && trainingDeleted?.some((el) => el._id === rowInEditionMode._id)) {
				cleanUpEditionMode(rowInEditionMode, "delete");
			} else {
				cleanUpEditionMode();
			}
		}
	});

	serviceTrainingSubscriptions?.subscriptions?.onUpdateItem?.setCallback((result) => {
		const trainingUpdated = result.data?.[subscriptions.get("onUpdateItem")?.getter]?.items;
		if (trainingUpdated) {
			queryClient.setQueryData(["serviceTraining", props?.commissionCycleId, locationId], (cachedData) => {
				return {
					...cachedData,
					items: trainingUpdated,
				};
			});
			cleanUpEditionMode(trainingUpdated);
		}
	});

	// ----------------------- Queries / Mutations -----------------------
	const { mutate: mutateCreateTraining } = useMutation({
		mutationFn: (input) => createTrainingItem(props.locationId, props?.commissionCycleId, input),
	});
	const { mutate: mutateUpdateTraining } = useMutation({
		mutationFn: (input) => updateTrainingItem(props.locationId, props?.commissionCycleId, input.itemId, input.mutationInput),
	});
	const { mutate: mutateDeleteTraining } = useMutation({
		mutationFn: (id) => deleteTrainingItems(props.locationId, props?.commissionCycleId, id),
	});

	// Rows should be passed to a state to be able to update them without a side effect.
	// Mutate the cache to update the rows will work as well.
	const [rows, setRows] = useState([]);

	// Controls the edit modes using a state.
	const [rowModesModel, setRowModesModel] = useState({});

	// Controls the selected rows using a state.
	const [selectedRows, setSelectedRows] = useState([]);

	// State to know if some row is loading. So the add and delete buttons can be disabled.
	const [rowLoading, setRowLoading] = useState(false);

	const [rowEffectiveFrom, setRowEffectiveFrom] = useState(null);
	const [rowEffectiveTo, setRowEffectiveTo] = useState(null);

	// state to know if this associate has been selected already
	const [assocAlreadySelected, setAssocAlreadySelected] = useState([]);

	const [isOverlappingAssocError, setIsOverlappingAssocError] = useState(false);

	const errorMessagesRef = useRef({});

	// This its the correct way to listen to the edit mode changes.
	const editModeActive = useMemo(() => Object.values(rowModesModel)?.some(({ mode }) => mode === GridRowModes.Edit), [rowModesModel]);

	const cleanUpEditionMode = (item, operation) => {
		if (item && operation === "delete") {
			setRowModesModel((prev) => ({
				...prev,
				[item._id]: { mode: GridRowModes.View },
			}));
		}

		newlyAddedRow.current = null;
	};

	const handleRowEditStart = (params, event) => {
		if (editModeActive) {
			event.defaultPrevented = true;
			event.defaultMuiPrevented = true;
		}
	};

	const handleRowEditStop = (params, event) => {
		event.defaultMuiPrevented = true;
	};

	// Thus fn is called when the user clicks on the "Save" button.
	const handleSaveClick = (id) => {
		const rowEdit = apiRef.current.state.editRows;
		const row = Object.values(rowEdit)[0];
		const keys = Object.keys(row);
		const errors = keys.filter((key) => row[key].error || !row[key].value);

		if (!errors?.length) {
			setRowModesModel((prev) => ({
				...prev,
				[id]: { mode: GridRowModes.View },
			}));
		} else if (errors?.length === keys.length) {
			apiRef.current.setState((state) => ({
				...state,
				editRows: {
					...state.editRows,
					[id]: {
						...state.editRows[id],
						...errors.reduce((acc, key) => ({ ...acc, [key]: { ...state.editRows[id][key], error: true } }), {}),
					},
				},
			}));
		}
	};

	// This fn is called when the user clicks on the "Cancel" button
	const handleCancelClick = (id) => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});

		const editedRow = rows.find((row) => row._id === id);
		if (editedRow?.isNew) {
			setRows(rows.filter((row) => row._id !== id));
		}
	};

	// This fn is called when the user clicks on the "Delete" button.
	const handleDeleteClick = async (ids) => {
		setRowLoading(true);

		ids.forEach((id) => {
			RowFeedback.skeleton(id, apiRef).delete().display();
		});

		mutateDeleteTraining(ids, {
			onSuccess: () => {
				setRows((prev) => prev.filter((row) => !ids.includes(row._id)));
				setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)));
			},
			onError: () => {
				ids.forEach((id) => {
					RowFeedback.skeleton(id, apiRef).errorDelete();
				});
			},
			onSettled: () => {
				setRowLoading(false);
			},
		});
	};

	// This fn is called when the user clicks on the "Add" button.
	const handleAddRow = () => {
		const key = uuidv4();
		const newRow = {
			_id: key,
			isNew: true,
			createdAt: new Date(),
			createdBy: user,
			updatedAt: new Date(),
			updatedBy: user,
			userId: null,
			effectiveDateFrom: null,
			effectiveDateTo: null,
			pay: {
				input: "Dollar",
				value: 0,
			},
		};

		newlyAddedRow.current = newRow;

		setRows((oldRows) => [newRow, ...oldRows]);
		setRowModesModel((oldModel) => ({
			...oldModel,
			[key]: { mode: GridRowModes.Edit },
		}));
	};

	// This fn is called when the user clicks on the "Add" button.
	const handleRowSelected = (newSelection) => {
		setSelectedRows(newSelection);
	};

	// This fn is call after the row is added or edited and it should be used to save data to the backend.
	const handleProcessRowUpdate = async (newRow, oldRow) => {
		// Compare the old row with the new row to know if changes were made.
		const isModified = Object.keys(newRow).some((key) => newRow[key] !== oldRow?.[key]);
		if (!isModified) {
			return oldRow;
		}

		const updatedRow = { ...newRow, isNew: false };
		// eslint-disable-next-line no-unused-vars
		const { pay, effectiveDateFrom, effectiveDateTo, userId: recipientUser, ...resultRow } = updatedRow;

		setRowLoading(true);

		// Here you should save the data to the backend.
		RowFeedback.skeleton(resultRow._id, apiRef).display();

		// Mutation operations
		let result = null;
		const mutateOperation = (id, apiRef, resolve) => ({
			onSuccess: (response) => {
				RowFeedback.skeleton(id, apiRef).success();
				resolve?.(response);
			},
			onSettled: () => {
				RowFeedback.skeleton(id, apiRef).success();
				setRowLoading(false);
				resolve(true);
			},
			onError: (err) => {
				RowFeedback.skeleton(id, apiRef).error();
				console.log(err);
			},
		});

		if (newRow?.isNew) {
			// Save
			const { _id, isNew, ...input } = newRow;
			newlyAddedRow.current = { ...newRow };

			const mutationInput = {
				...input,
				userId: recipientUser?._id,
				createdBy: input?.createdBy?._id,
				updatedBy: input?.updatedBy?._id,
			};
			result = new Promise((resolve) => {
				mutateCreateTraining(mutationInput, mutateOperation(_id, apiRef, resolve));
			});
		} else {
			// Update
			newlyAddedRow.current = { ...updatedRow };
			const { _id, isNew, ...input } = newRow;
			const mutationInput = {
				...input,
				userId: recipientUser?._id,
				createdBy: input?.createdBy?._id,
				updatedBy: user?._id,
				updatedAt: new Date(),
			};

			// eslint-disable-next-line no-unused-vars
			const updateArgs = { mutationInput, itemId: _id };
			result = new Promise((resolve) => {
				mutateUpdateTraining(updateArgs, mutateOperation(_id, apiRef, resolve));
			});
		}

		if (result) {
			await result;
		}
		setRows(rows.map((row) => (row._id === newRow._id ? newRow : row)));
		return resultRow;
	};

	// This fn is call if an error was thrown when sending data to the backend.
	const handleProcessRowUpdateError = (error) => {
		// Here you should handle the error.
		console.error(error);
	};

	function formatHours(value) {
		const hours = Math.floor(value);
		const minutes = Math.round((value - hours) * 60);
		return `${hours}h ${minutes}m`;
	}

	const isValueInRange = (input, value) => {
		// Get the range for the input
		const range = INPUT_RANGES[input];

		// Check if the value is within the range
		return value >= range.min && value <= range.max;
	};

	function getErrorMessage({ valueExists, valueIsDate, valueIsInRange, isOverlappingError }) {
		if (!valueExists) {
			return "Value is required.";
		} else if (!valueIsDate) {
			return "Value must be a valid date.";
		} else if (!valueIsInRange) {
			return "Value must be within the valid range.";
		} else if (isOverlappingError) {
			return "Value is overlapping with another value.";
		} else {
			return "";
		}
	}

	const columns = [
		{ field: "_id", headerName: "ID", width: 50, filterable: false },
		{
			field: "updatedAt",
			headerName: "Last Updated At",
			width: ONE_HUNDRED_EIGHTY,
			type: "date",
			sortable: true,
			resizable: false,
			valueFormatter: (value) => getFormattedDate(value, timezone, DATE_FORMAT.fullAbbrTime12hPadded),
			renderCell: (row) => <CustomCell value={row.formattedValue ?? row.value} hasTooltip maxWith={row.colDef.width} />,
		},
		{
			field: "updatedBy",
			headerName: "Updated By",
			width: ONE_HUNDRED_EIGHTY,
			minWidth: ONE_HUNDRED_EIGHTY,
			maxWidth: ONE_HUNDRED_EIGHTY * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: true,
			valueFormatter: (value) => {
				if (value?.fullName) {
					return value.fullName;
				}
				return value;
			},
			renderCell: (row) => <CustomCell value={row.formattedValue ?? row.value} hasTooltip maxWith={row.colDef.width} />,
		},
		{
			field: "createdAt",
			headerName: "",
			width: 10,
			type: "date",
			hide: true,
			filterable: false,
			valueGetter: (value) => new Date(value),
		},
		{
			field: "createdBy",
			headerName: "",
			width: 10,
			hide: true,
			filterable: false,
		},
		{
			field: "userId",
			headerName: "Assoc. Name",
			width: ONE_HUNDRED_EIGHTY,
			minWidth: ONE_HUNDRED_EIGHTY,
			maxWidth: ONE_HUNDRED_EIGHTY * COLUMN_RESIZE_FACTOR,
			sortable: true,
			editable: isColEditable,
			resizable: true,
			renderEditCell: (params) => (
				<InputEditUserCell
					{...params}
					setAssocAlreadySelected={setAssocAlreadySelected}
					error={!params.row.userId || params.error}
					rows={rows}
				/>
			),
			preProcessEditCellProps: (params) => {
				if (params.props) {
					const valueExists = emptyValidation(params.props.value);
					return { ...params.props, error: !valueExists };
				}

				return { error: true };
			},
			valueFormatter: (value) => {
				if (value && typeof value.fullName !== "undefined" && typeof value.employeeId !== "undefined") {
					return `${value.fullName} (${value.employeeId})`;
				}
				return value;
			},
			renderCell: (row) => (
				<CustomCell
					value={row?.formattedValue ?? `${row.formattedValuerow?.value?.fullName} (${row?.value?.employeeId})`}
					hasTooltip
					maxWith={row.colDef.width}
				/>
			),
		},
		{
			field: "effectiveDateFrom",
			headerName: "Effective From",
			width: ONE_HUNDRED_FIFTY,
			type: "date",
			sortable: true,
			editable: isColEditable,
			resizable: false,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
			renderEditCell: (params) => (
				<InputEditDatetimeCell
					{...params}
					rowEffectiveDateTo={rowEffectiveTo}
					rowEffectiveDateFrom={rowEffectiveFrom}
					assocAlreadySelected={assocAlreadySelected}
					setRowEffectiveDate={setRowEffectiveFrom}
					setOverlappingError={setIsOverlappingAssocError}
					endDate={params.row.effectiveDateTo}
					startDateRange={props.effectiveDateRangeFrom}
					endDateRange={props.effectiveDateRangeTo}
					error={!params.row.effectiveDateFrom || params.error}
					errorMessage={errorMessagesRef.current?.[params.id]?.["effectiveDateFrom"]}
				/>
			),
			preProcessEditCellProps: (params) => {
				if (params.props) {
					const valueExists = emptyValidation(params.props.value);
					const valueIsDate = dateValidation(params.props.value);

					const inputValue = moment(params.props.value).format();
					const momentValue = moment(inputValue);

					const isSameOrAfter = momentValue.isSameOrAfter(props.effectiveDateRangeFrom);
					const isSameOrBefore = momentValue.isSameOrBefore(props.effectiveDateRangeTo);
					const isSameOrBeforeEffectiveDateTo = momentValue.isSameOrBefore(rowEffectiveTo);

					const valueIsInRange = isSameOrAfter && isSameOrBefore && isSameOrBeforeEffectiveDateTo;

					const errorMessage = getErrorMessage({
						valueExists,
						valueIsDate,
						valueIsInRange,
						isOverlappingError: isOverlappingAssocError,
					});
					errorMessagesRef.current[params.id] = { ...errorMessagesRef.current[params.id], effectiveDateFrom: errorMessage };

					return {
						...params.props,
						error: !valueExists || !valueIsDate || !valueIsInRange || isOverlappingAssocError,
					};
				}

				return { error: true };
			},
			renderCell: (row) => <CustomCell value={row.formattedValue ?? row.value} hasTooltip maxWith={row.colDef.width} />,
		},
		{
			field: "effectiveDateTo",
			headerName: "Effective To",
			width: ONE_HUNDRED_FIFTY,
			type: "date",
			sortable: true,
			editable: isColEditable,
			resizable: false,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
			renderEditCell: (params) => (
				<InputEditDatetimeCell
					{...params}
					rowEffectiveDateTo={rowEffectiveTo}
					rowEffectiveDateFrom={rowEffectiveFrom}
					assocAlreadySelected={assocAlreadySelected}
					setRowEffectiveDate={setRowEffectiveTo}
					setOverlappingError={setIsOverlappingAssocError}
					startDate={params.row.effectiveDateFrom}
					startDateRange={props.effectiveDateRangeFrom}
					endDateRange={props.effectiveDateRangeTo}
					error={!params.row.effectiveDateTo || params.error}
					errorMessage={errorMessagesRef.current?.[params.id]?.["effectiveDateTo"]}
				/>
			),
			preProcessEditCellProps: (params) => {
				if (params.props) {
					const valueExists = emptyValidation(params.props.value);
					const valueIsDate = dateValidation(params.props.value);

					const inputValue = moment(params.props.value).format();
					const momentValue = moment(inputValue);

					const isSameOrAfter = momentValue.isSameOrAfter(props.effectiveDateRangeFrom);
					const isSameOrBefore = momentValue.isSameOrBefore(props.effectiveDateRangeTo);
					const isSameOrAfterEffectiveDateFrom = momentValue.isSameOrAfter(rowEffectiveFrom);

					const valueIsInRange = isSameOrAfter && isSameOrBefore && isSameOrAfterEffectiveDateFrom;

					const error = !valueExists || !valueIsDate || !valueIsInRange || isOverlappingAssocError;
					const errorMessage = getErrorMessage({
						valueExists,
						valueIsDate,
						valueIsInRange,
						isOverlappingError: isOverlappingAssocError,
					});
					errorMessagesRef.current[params.id] = { ...errorMessagesRef.current[params.id], effectiveDateTo: errorMessage };

					return { ...params.props, error };
				}

				return { error: true };
			},
		},
		{
			field: "pay",
			headerName: "Pay/Hours",
			width: ONE_HUNDRED_TWENTY,
			sortable: false,
			editable: isColEditable,
			resizable: false,
			renderEditCell: (params) => (
				<InputEditPayCell
					{...params}
					error={!params.row.pay || params.row.pay.value === 0 || params.error}
					errorMessage={errorMessagesRef.current?.[params.id]?.["pay"]}
				/>
			),
			preProcessEditCellProps: (params) => {
				if (params.props) {
					const valueExists = emptyValidation(params.props.value);
					const valueIsInRange = isValueInRange(params.props.value.input, params.props.value.value);

					const error = !valueExists || !valueIsInRange;
					const errorMessage = getErrorMessage({
						valueExists,
						valueIsInRange,
						valueIsDate: true,
						isOverlappingError: false,
					});
					errorMessagesRef.current[params.id] = { ...errorMessagesRef.current[params.id], pay: errorMessage };

					return { ...params.props, error };
				}

				return { error: true };
			},
			valueFormatter: (value) => {
				switch (value.input) {
					case "Dollar":
						return {
							symbol: "$",
							value: value?.value ? Number(value.value).toFixed(2) : Number(0).toFixed(2),
						};
					case "Duration":
						return {
							symbol: "",
							value: formatHours(value.value),
						};
					default:
						return value;
				}
			},
			renderCell: (params) => {
				return (
					<div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
						<span>{params.formattedValue.symbol}</span>
						<span>{params.formattedValue.value}</span>
					</div>
				);
			},
		},
		{
			field: "actions",
			type: "actions",
			width: 70,
			resizable: false,
			getActions: ({ id }) => {
				const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
				if (isInEditMode) {
					return [
						<GridActionsCellItem
							key="saveTraining"
							icon={<SaveIconComponent />}
							label="Save"
							onClick={() => handleSaveClick(id)}
						/>,
						<GridActionsCellItem
							key="cancel"
							icon={<CancelIcon />}
							label="Cancel"
							className="textPrimary"
							onClick={() => handleCancelClick(id)}
							color="inherit"
						/>,
					];
				}

				return [];
			},
		},
	];

	const isComponentLoading = isLoading || rowLoading;

	useEffect(() => {
		const newRow = rows.find((row) => row.isNew);

		if (editModeActive) {
			setRows(compact([newRow, ...props.data]));
		} else {
			setRows(compact(props.data));
		}
		return () => {
			setRows([]);
		};
	}, [props, props.data]);

	return (
		<DataSmartTable
			columns={columns}
			rows={rows}
			rowHeight={28}
			apiRef={apiRef}
			editMode="row"
			maxRows={14}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			checkboxSelection
			autoHeight={false}
			isRowSelectable={() => !editModeActive && Boolean(props.currentMonth && editTraining)}
			onRowSelectionModelChange={handleRowSelected}
			rowModesModel={rowModesModel}
			processRowUpdate={handleProcessRowUpdate}
			onProcessRowUpdateError={handleProcessRowUpdateError}
			onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
			onRowEditStart={handleRowEditStart}
			onRowEditStop={handleRowEditStop}
			columnVisibilityModel={{
				_id: false,
				createdAt: false,
				createdBy: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					customComponent: <HelpTooltip title={HELP_TOOLTIP_TITLE} />,
					filter: {
						hide: !!selectedRows.length,
						disabled: editModeActive,
					},
					search: {
						columns,
						data: rows,
						hide: !!selectedRows.length,
						disabled: editModeActive,
					},
					add: {
						onAddAction: handleAddRow,
						hide: !!selectedRows.length,
						disabled: editModeActive || !props.currentMonth || !editTraining || isComponentLoading,
					},
					delete: {
						onDeleteAction: handleDeleteClick,
						deleteTitle: "Remove",
						hide: !selectedRows.length,
						disabled: !props.currentMonth || !editTraining || isComponentLoading,
					},
				},
				noRowsOverlay: {
					title: "No Training Data",
				},
			}}
			hideFooter
			customProps={{
				tableClassName: {
					"functional-table": true,
					"edit-table": true,
					...TAILWIND_FOR_DATA_SMART_TABLE,
				},
				expandableRows: false,
				hide: !rows?.length,
				tableHidedProps: {
					currentMonth: props.currentMonth && editTraining,
					sectionFirstTitle: "No Training Data.",
					onAddTheFirstOne: handleAddRow,
				},
			}}
		/>
	);
}

export default function TrainingSectionTable(props) {
	if (props.loading || !props.locationId) {
		return null;
	}

	return <>{props.dataPresent && <TrainingTable {...props} />}</>;
}

TrainingSectionTable.propTypes = {
	data: PropTypes.array.isRequired,
	commissionCycleId: PropTypes.string.isRequired,
	effectiveDateRangeFrom: PropTypes.string.isRequired,
	effectiveDateRangeTo: PropTypes.string.isRequired,
	loading: PropTypes.bool.isRequired,
	locationId: PropTypes.string.isRequired,
	dataPresent: PropTypes.bool.isRequired,
	currentMonth: PropTypes.bool.isRequired,
};

TrainingTable.propTypes = {
	data: PropTypes.array.isRequired,
	commissionCycleId: PropTypes.string.isRequired,
	effectiveDateRangeFrom: PropTypes.string.isRequired,
	effectiveDateRangeTo: PropTypes.string.isRequired,
	loading: PropTypes.bool.isRequired,
	locationId: PropTypes.string.isRequired,
	dataPresent: PropTypes.bool.isRequired,
	currentMonth: PropTypes.bool.isRequired,
};
